import { useCallback } from 'react';
import { useApolloClient } from '@apollo/client/react';
import { ASSETS_BY_IDS } from '@scio3/graphql-client';
import { useRealtimeSubscription } from '@/hooks/useRealtimeSubscription';
import { useTeamStore } from '@/stores/team-store';
import { GROUP_TYPES } from '@scio3/shared-types';

/**
 * Subscribes to real-time asset changes via WebSocket.
 *
 * Remote UPDATE events trigger a batch multi-fetch by IDs (Notion-style):
 * IDs are collected in a 500ms dedup window, then fetched in one assetsByIds
 * query. The response returns Asset[] (same __typename as list) so Apollo
 * auto-merges into the normalized cache without cache.modify().
 *
 * ADD/DELETE events trigger a debounced full list refetch.
 * Also subscribes to GROUP events (subType 'asset') for folder changes.
 * Falls back to window-focus refetch when WS is unavailable.
 */
export function useAssetSubscriptions(refetch: () => void, refetchGroups?: () => void) {
  const client = useApolloClient();
  const { activeTeamId } = useTeamStore();

  const batchFetchByIds = useCallback(
    async (ids: string[]) => {
      if (!activeTeamId) return;
      await client.query({
        query: ASSETS_BY_IDS,
        variables: { ids, teamId: activeTeamId },
        fetchPolicy: 'network-only',
      });
      // Apollo auto-merges Asset[] into normalized cache by __typename:_id
    },
    [client, activeTeamId],
  );

  useRealtimeSubscription('asset', refetch, { batchFetchByIds });

  // Subscribe to folder (group) changes from other users
  useRealtimeSubscription('group', refetchGroups ?? refetch, {
    subType: GROUP_TYPES.ASSET,
    refetchOnUpdate: true,
  });
}
